import { DownloadOutlined } from '@ant-design/icons'
import { Button, Card, Empty, Flex, Segmented, Typography } from 'antd'

import { ChartBody } from '@/components/charts/chartTheme'

import type { ChargeView } from '../useBusinessFilters'
import type { ChargeWindow } from '../useBusinessQueries'
import { ChargesChart } from './ChargesChart'
import type { ChargeScope } from './chargeScope'
import { chargesCsv, downloadCsv } from './chargesCsv'
import { ChargesTables } from './ChargesTables'
import { type AdminCharges, chargeSeries } from './chargeSummary'

export function ChargesSection({
  charges,
  scope,
  loading,
  days,
  step,
  view,
  onViewChange,
}: {
  charges?: AdminCharges
  scope: ChargeScope
  loading: boolean
  days: number
  step: ChargeWindow
  view: ChargeView
  onViewChange: (view: ChargeView) => void
}) {
  return (
    <>
      <Card title="Charges by product" style={{ marginTop: 16 }}>
        <Flex justify="end" wrap gap={8} style={{ marginBottom: 8 }}>
          <Segmented<ChargeView>
            aria-label="Chart values"
            value={view}
            onChange={onViewChange}
            options={[
              { label: 'Amount', value: 'amount' },
              { label: 'Share', value: 'share' },
            ]}
          />
          <Button
            icon={<DownloadOutlined />}
            disabled={!charges?.buckets.length}
            onClick={() => {
              if (!charges) return
              downloadCsv(
                `charges-${scope}-${days}d-${step.toLowerCase()}.csv`,
                chargesCsv(charges),
              )
            }}
          >
            CSV
          </Button>
        </Flex>
        <ChartBody
          data={charges}
          loading={loading}
          errorMessage="Failed to load charges"
        >
          {(data) =>
            data.buckets.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No charges in this period"
              />
            ) : (
              <ChargesChart
                points={chargeSeries(data)}
                window={step}
                range={data}
                share={view === 'share'}
              />
            )
          }
        </ChartBody>
        <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
          Periods follow the time a charge was sent to OpenMeter, not its
          billing period. The first and last periods are partial: the range ends
          now.
        </Typography.Paragraph>
      </Card>
      {charges && charges.buckets.length > 0 && (
        <ChargesTables charges={charges} scope={scope} />
      )}
    </>
  )
}
